import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles } from 'lucide-react';
import { HERO_VIDEO_URL } from '@/config/marketing';

export default function HeroSection() {
    const { t } = useTranslation(['admin']);
    const [videoFailed, setVideoFailed] = useState(false);
    const scrollToContact = () => {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <section className="relative min-h-[100svh] pt-24 md:pt-28 flex items-center justify-center overflow-hidden bg-[#0a0a0a]">
            {/* Background Video */}
            {/* QA-W6-14: if the video cannot load, a still background takes its place. */}
            <div className="absolute inset-0 z-0">
                {videoFailed ? (
                    <div
                        className="w-full h-full bg-gradient-to-br from-[#1e0b33] via-[#0a0a0a] to-[#0a0a0a]"
                        data-testid="hero-still-background"
                        aria-hidden
                    />
                ) : (
                    <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="none"
                        poster="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGMAAQAABQABDQottAAAAABJRU5ErkJggg=="
                        className="w-full h-full object-cover"
                        onError={() => setVideoFailed(true)}
                        aria-hidden
                    >
                        {/* A failed <source> fires its own error, not the video's. */}
                        <source src={HERO_VIDEO_URL} type="video/mp4" onError={() => setVideoFailed(true)} />
                    </video>
                )}
                <div className="absolute inset-0 bg-black/70" />
            </div>
            

            {/* Mobile gradient overlay to preserve original color */}
            

            {/* Mobile gradient overlay to preserve original color */}
            <div className="absolute inset-0 z-[1] md:hidden pointer-events-none" aria-hidden>
                <div className="absolute inset-0 bg-gradient-to-b from-[#3b0b50] via-transparent to-transparent opacity-70" />
            </div>

            {/* Animated Gradient Overlay */}
            <div className="absolute inset-0 z-[2] overflow-hidden pointer-events-none opacity-30 md:opacity-100">
                <div className="absolute top-1/4 left-1/4 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-purple-600/20 rounded-full blur-[40px] md:blur-[120px] animate-pulse" />
                <div className="absolute bottom-1/4 right-1/4 w-[250px] md:w-[400px] h-[250px] md:h-[400px] bg-blue-600/15 rounded-full blur-[30px] md:blur-[100px] animate-pulse delay-1000" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] md:w-[600px] h-[350px] md:h-[600px] bg-fuchsia-600/10 rounded-full blur-[50px] md:blur-[150px]" />
            </div>

            {/* Noise Texture */}
            <div className="absolute inset-0 z-[3] opacity-[0.03]" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
            }} />

            {/* Content */}
            <div className="relative z-10 max-w-6xl mx-auto px-6 text-center pb-28 sm:pb-20">
                {/* Badge */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-8"
                >
                    <Sparkles className="w-4 h-4 text-purple-400" />
                    <span className="text-sm text-white/70">{t('admin:landing.hero.badge')}</span>
                </motion.div>

                {/* Main Headline */}
                <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-[5.5rem] font-bold tracking-tight leading-[1.05] mb-6 md:mb-8">
                    <span className="text-white">{t('admin:landing.hero.headlinePart1')}</span>
                                                <br />
                                                <span className="bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent">
                                                    {t('admin:landing.hero.headlinePart2')}
                                                </span>
                </h1>

                {/* Subheadline */}
                <p className="text-base sm:text-lg md:text-xl text-white/50 max-w-xl mx-auto mb-8 md:mb-12 font-light leading-relaxed px-4 sm:px-0">
                    {t('admin:landing.hero.subheadline')}
                </p>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <Button
                        onClick={scrollToContact}
                        size="lg"
                        className="h-14 px-8 bg-white text-black hover:bg-white/90 rounded-full text-base font-medium group"
                    >
                        {t('admin:landing.hero.bookMeeting')}
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="lg"
                        className="h-14 px-8 text-white/60 hover:text-white hover:bg-white/5 rounded-full text-base"
                        onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                    >
                        {t('admin:landing.hero.discover')}
                    </Button>
                </div>

                
            </div>

            {/* Scroll Indicator */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
                className="absolute left-1/2 -translate-x-1/2"
                style={{
                    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                    bottom: 'max(env(safe-area-inset-bottom, 0px), 1.5rem)',
                }}
            >
                <motion.div
                    animate={{ y: [0, 8, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="w-5 h-8 rounded-full border border-white/20 flex items-start justify-center p-1.5"
                >
                    <div className="w-1 h-1.5 rounded-full bg-white/40" />
                </motion.div>
            </motion.div>
        </section>
    );
}